import { supabase } from '@/lib/supabase'
import type { Attachment, ConversationSummary, MessageRow } from '@/types/database'

export const PAGE_SIZE = 40

export async function listConversations(before?: string): Promise<ConversationSummary[]> {
  const { data, error } = await supabase.rpc('list_conversations', {
    before: before ?? null,
    max_rows: 30,
  })
  if (error) throw error
  return data ?? []
}

/** Newest-first from the server; the UI reverses once for render. */
export async function fetchMessages(
  conversationId: string,
  before?: string,
): Promise<MessageRow[]> {
  let q = supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(PAGE_SIZE)

  if (before) q = q.lt('created_at', before)

  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as MessageRow[]
}

export interface SendMessageInput {
  conversationId: string
  senderId: string
  body?: string
  attachments?: Attachment[]
  replyToId?: string | null
  /** Overrides the kind inferred from the attachments. */
  kind?: MessageRow['kind']
  /** Stable across retries so a flaky network cannot double-post. */
  clientNonce: string
}

export async function sendMessage(input: SendMessageInput): Promise<MessageRow> {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      conversation_id: input.conversationId,
      sender_id: input.senderId,
      kind: input.kind ?? (input.attachments?.length ? 'media' : 'text'),
      body: input.body?.trim() || null,
      attachments: input.attachments ?? [],
      reply_to_id: input.replyToId ?? null,
      client_nonce: input.clientNonce,
    })
    .select('*')
    .single()

  if (error) {
    // 23505 = the nonce unique index fired: this exact message already landed
    // on a previous attempt, so treat the retry as a success.
    if (error.code === '23505') {
      const { data: existing } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', input.conversationId)
        .eq('client_nonce', input.clientNonce)
        .single()
      if (existing) return existing as MessageRow
    }
    throw error
  }
  return data as MessageRow
}

export async function deleteMessage(messageId: string) {
  const { error } = await supabase
    .from('messages')
    .update({ deleted_at: new Date().toISOString(), body: null, attachments: [] })
    .eq('id', messageId)
  if (error) throw error
}

export async function toggleReaction(messageId: string, userId: string, emoji: string) {
  const { data: existing } = await supabase
    .from('message_reactions')
    .select('emoji')
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .eq('emoji', emoji)
    .maybeSingle()

  if (existing) {
    await supabase
      .from('message_reactions')
      .delete()
      .eq('message_id', messageId)
      .eq('user_id', userId)
      .eq('emoji', emoji)
  } else {
    await supabase
      .from('message_reactions')
      .insert({ message_id: messageId, user_id: userId, emoji })
  }
}

export async function markRead(conversationId: string) {
  const { error } = await supabase.rpc('mark_conversation_read', { cid: conversationId })
  if (error) throw error
}

export async function startDirectConversation(otherUserId: string): Promise<string> {
  const { data, error } = await supabase.rpc('start_direct_conversation', {
    other_user: otherUserId,
  })
  if (error) throw error
  return data as string
}

export async function createGroup(title: string, memberIds: string[]): Promise<string> {
  const { data, error } = await supabase.rpc('create_group', {
    title,
    member_ids: memberIds,
  })
  if (error) throw error
  return data as string
}

/** Upload into the private `chat` bucket, keyed by conversation. */
export async function uploadChatAttachment(
  conversationId: string,
  file: File,
): Promise<Attachment> {
  const ext = file.name.split('.').pop() ?? 'bin'
  const path = `${conversationId}/${crypto.randomUUID()}.${ext}`
  const { error } = await supabase.storage
    .from('chat')
    .upload(path, file, { contentType: file.type, upsert: false })
  if (error) throw error
  // The object key is a random uuid, not the sender's filename -- keep the
  // real one, or a file bubble has nothing to show but an extension.
  return { path, mime: file.type || 'application/octet-stream', name: file.name, size: file.size }
}

/**
 * Voice notes go in the same private bucket as other chat media. The duration
 * is carried on the attachment because the recorded container has none in its
 * header -- without it the player cannot show a length until the whole file
 * has been played.
 */
export async function uploadVoiceNote(
  conversationId: string,
  blob: Blob,
  mimeType: string,
  durationMs: number,
): Promise<Attachment> {
  const ext = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('ogg') ? 'ogg' : 'webm'
  const path = `${conversationId}/${crypto.randomUUID()}.${ext}`
  const { error } = await supabase.storage
    .from('chat')
    .upload(path, blob, { contentType: mimeType, upsert: false })
  if (error) throw error
  return { path, mime: mimeType, duration_ms: Math.round(durationMs) }
}

export async function signedAttachmentUrl(path: string, ttlSeconds = 3600) {
  const { data, error } = await supabase.storage.from('chat').createSignedUrl(path, ttlSeconds)
  if (error) throw error
  return data.signedUrl
}

/**
 * The batch form of the above, for a screen that renders many thumbnails at
 * once: one request for a page of media instead of one per tile. A path that
 * fails to sign (deleted from storage, say) is simply missing from the map
 * rather than failing the whole page.
 */
export async function signedAttachmentUrls(
  paths: string[],
  ttlSeconds = 3600,
): Promise<Map<string, string>> {
  if (paths.length === 0) return new Map()
  const { data, error } = await supabase.storage.from('chat').createSignedUrls(paths, ttlSeconds)
  if (error) throw error
  const entries = data
    .filter((d) => Boolean(d.path && d.signedUrl))
    .map((d) => [d.path as string, d.signedUrl as string] as const)
  return new Map(entries)
}

export const MEDIA_PAGE_SIZE = 30

/**
 * Every image, video and file ever sent in a conversation, newest first.
 * Voice notes are excluded -- they are heard, not browsed -- and a deleted
 * message's attachment does not resurface here even though the row itself is
 * kept for the thread's "this message was deleted" placeholder.
 *
 * Filtered in SQL by `attachments <> '[]'`, not by mime: a message's
 * attachments are a small jsonb array on the row, not worth a dedicated RPC or
 * a second table -- the same RLS that protects `fetchMessages` already
 * protects this, since it is the same table under the same policy. Splitting
 * "media" from "files" happens client-side, on the same fetched page, because
 * that split is about what is inside the jsonb array, not something a
 * PostgREST filter expresses cleanly.
 */
export type MediaMessage = Pick<MessageRow, 'id' | 'attachments' | 'created_at'>

export async function fetchConversationMedia(
  conversationId: string,
  before?: string,
): Promise<MediaMessage[]> {
  let q = supabase
    .from('messages')
    .select('id, attachments, created_at')
    .eq('conversation_id', conversationId)
    .is('deleted_at', null)
    .neq('kind', 'voice')
    // .neq() is typed against the column's row type (Attachment[]), which a
    // JSON literal can never satisfy; .filter() bypasses that and sends the
    // same `attachments=neq.[]` PostgREST understands.
    .filter('attachments', 'neq', '[]')
    .order('created_at', { ascending: false })
    .limit(MEDIA_PAGE_SIZE)

  if (before) q = q.lt('created_at', before)

  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as MediaMessage[]
}

// ---------------------------------------------------------------------------
// Group administration
//
// These are RPCs rather than client-side sequences: each needs several writes
// to land together, and the permission rules belong in one place rather than
// in whichever screen happens to call them.
// ---------------------------------------------------------------------------

export async function renameConversation(conversationId: string, title: string) {
  const { error } = await supabase.rpc('rename_conversation', {
    cid: conversationId,
    new_title: title,
  })
  if (error) throw error
}

export async function addConversationMembers(conversationId: string, memberIds: string[]) {
  const { data, error } = await supabase.rpc('add_conversation_members', {
    cid: conversationId,
    member_ids: memberIds,
  })
  if (error) throw error
  return data as number
}

export async function removeConversationMember(conversationId: string, userId: string) {
  const { error } = await supabase.rpc('remove_conversation_member', {
    cid: conversationId,
    target: userId,
  })
  if (error) throw error
}

export async function setMemberRole(
  conversationId: string,
  userId: string,
  role: 'admin' | 'member',
) {
  const { error } = await supabase.rpc('set_member_role', {
    cid: conversationId,
    target: userId,
    new_role: role,
  })
  if (error) throw error
}

/** Pass null to unmute. */
export async function setConversationMuted(conversationId: string, until: Date | null) {
  const { error } = await supabase.rpc('set_conversation_muted', {
    cid: conversationId,
    until: until ? until.toISOString() : null,
  })
  if (error) throw error
}

export async function getMyMembership(conversationId: string, userId: string) {
  const { data, error } = await supabase
    .from('conversation_members')
    .select('role, muted_until')
    .eq('conversation_id', conversationId)
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return data
}
